import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import useCurrentUser from '../hooks/useCurrentUser';
import { BILLING_BLOCKED_STATUSES, getPaymentPendingRedirect } from '../utils/payment-pending';
import usePlatformActivation from '../hooks/usePlatformActivation';

const RequireAuth = () => {
  const location = useLocation();
  const { tokens, user, isHydrated, isSessionRestoredFromStorage, onboardingTarget } = useAuth();
  const currentDestination = location.pathname + location.search + location.hash;
  const currentUser = useCurrentUser();
  const fetchedUser = currentUser.isFetchedAfterMount && currentUser.data ? currentUser.data : null;
  const effectiveUser = fetchedUser && user?.id === fetchedUser.id
    ? {
      ...fetchedUser,
      requiresConsent: fetchedUser.requiresConsent ?? user.requiresConsent,
      restrictedAccess: fetchedUser.restrictedAccess ?? user.restrictedAccess,
      pendingConsents: fetchedUser.pendingConsents ?? user.pendingConsents,
    }
    : fetchedUser ?? user ?? currentUser.data;
  const platformActivation = usePlatformActivation(effectiveUser);
  const isOnConsentPage = location.pathname.startsWith('/consent-review');
  const isOnPendingSetupPage = location.pathname.startsWith('/billing/pending-setup');
  const isOnCheckoutStartPage = location.pathname.startsWith('/billing/checkout/start');
  const isOnSettingsPage = location.pathname.startsWith('/settings');
  const isOnPlatformActivationPage =
    location.pathname.startsWith('/platform-activation') ||
    location.pathname.startsWith('/platforms-onboarding');
  const isLegacyCheckoutCallback =
    location.pathname === '/' &&
    ['success', 'cancelled'].includes((new URLSearchParams(location.search).get('checkout') ?? '').toLowerCase());
  const requiresConsent = Boolean(effectiveUser && (effectiveUser.requiresConsent || effectiveUser.restrictedAccess));
  const paymentPendingRedirect = getPaymentPendingRedirect(effectiveUser);
  const requiresPaymentResolution = Boolean(paymentPendingRedirect);
  const hasBillingBlockedSubscription = BILLING_BLOCKED_STATUSES.has(
    String(effectiveUser?.subscriptionStatus ?? '').toLowerCase(),
  );
  const canResolvePaymentHere = paymentPendingRedirect === '/billing/pending-setup'
    ? isOnPendingSetupPage || isOnCheckoutStartPage || isLegacyCheckoutCallback || (hasBillingBlockedSubscription && isOnSettingsPage)
    : isOnCheckoutStartPage || isLegacyCheckoutCallback;
  const canRenderDuringCurrentUserRefresh = Boolean(user) && !isSessionRestoredFromStorage;
  const isCheckingCurrentUser =
    currentUser.isFetching &&
    !currentUser.isFetchedAfterMount &&
    !canRenderDuringCurrentUserRefresh &&
    !requiresConsent &&
    !requiresPaymentResolution;

  if (!isHydrated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-default text-text-primary">
        <div className="animate-pulse text-sm text-text-muted">Loading session…</div>
      </div>
    );
  }

  if (!tokens?.accessToken) {
    let manualLogout = false;
    if (typeof window !== 'undefined') {
      manualLogout = window.sessionStorage.getItem('sf-manual-logout') === '1';
      if (manualLogout) {
        window.sessionStorage.removeItem('sf-manual-logout');
      }
    }

    if (manualLogout) {
      return <Navigate to="/login" replace />;
    }

    const redirectTo = encodeURIComponent(currentDestination);
    return <Navigate to={`/login?redirectTo=${redirectTo}`} replace />;
  }

  if (isCheckingCurrentUser) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-default text-text-primary">
        <div className="animate-pulse text-sm text-text-muted">Checking account status…</div>
      </div>
    );
  }

  if (requiresConsent && !isOnConsentPage) {
    const redirectTo = encodeURIComponent(currentDestination);
    return <Navigate to={`/consent-review?redirectTo=${redirectTo}`} replace />;
  }

  if (!requiresConsent && isOnConsentPage) {
    const params = new URLSearchParams(location.search);
    const requested = params.get('redirectTo');
    const safeDestination = requested && requested.startsWith('/') ? requested : '/';
    return <Navigate to={safeDestination} replace />;
  }

  if (!requiresConsent && requiresPaymentResolution && !canResolvePaymentHere) {
    return <Navigate to={paymentPendingRedirect ?? '/billing/pending-setup'} replace />;
  }

  const shouldHonorPlatformOnboardingTarget =
    onboardingTarget === 'platforms' &&
    !platformActivation.isLoading &&
    !platformActivation.isError &&
    !platformActivation.hasConnectedPlatforms &&
    !platformActivation.hasCompletedOnboarding &&
    !platformActivation.isKnownWorkspace;

  if (!requiresConsent && !requiresPaymentResolution && shouldHonorPlatformOnboardingTarget && !isOnPlatformActivationPage) {
    return <Navigate to="/platform-activation" replace />;
  }

  if (!requiresConsent && !requiresPaymentResolution && platformActivation.shouldShowInterstitial && !isOnPlatformActivationPage) {
    return <Navigate to="/platform-activation" replace />;
  }

  return <Outlet />;
};

export default RequireAuth;
